/**
 * 💳 PaymentModal.jsx - Composant de paiement SerdiPay
 * 
 * Affiche une modal pour sélectionner l'opérateur et initier le paiement
 * Supporte: Airtel Money, Orange Money, Vodacom-Mpesa, Afrimoney
 */

import React, { useState, useCallback } from 'react';
import { Phone, X, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import apiService from './apiService';

const PAYMENT_STATES = {
  FORM: 'form',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
};

const POLLING_INTERVAL = 5000;
const POLLING_MAX_TRIES = 60;

const OPERATORS = {
  AM: { name: 'Airtel Money', icon: '🔴', bg: '#ff000015', border: '#e53935', text: '#c62828' },
  OM: { name: 'Orange Money', icon: '🟠', bg: '#ff980015', border: '#fb8c00', text: '#e65100' },
  MP: { name: 'Vodacom M-Pesa', icon: '🟢', bg: '#4caf5015', border: '#43a047', text: '#2e7d32' },
  AF: { name: 'Afrimoney', icon: '🔵', bg: '#2196f315', border: '#1e88e5', text: '#1565c0' },
};

export default function PaymentModal({
  isOpen = false,
  planName = 'BASIC',
  amount = 25000,
  duration = 30,
  onSuccess = () => {},
  onCancel = () => {},
  rideId = null,
}) {
  const [state, setState] = useState(PAYMENT_STATES.FORM);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentId, setPaymentId] = useState(null); // eslint-disable-line no-unused-vars
  const [error, setError] = useState(null);
  const [pollingTries, setPollingTries] = useState(0);
  const [selectedOperator, setSelectedOperator] = useState('AM');

  const formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 9 && !cleaned.startsWith('243')) {
      cleaned = cleaned.startsWith('0') ? '243' + cleaned.substring(1) : '243' + cleaned;
    }
    return cleaned.length > 0 ? '+' + cleaned : cleaned;
  };

  const handlePhoneChange = (e) => setPhoneNumber(formatPhoneNumber(e.target.value));

  const checkPaymentStatus = useCallback(
    async (payId, tries = 0) => {
      if (tries > POLLING_MAX_TRIES) {
        setError('Le paiement a dépassé le délai d\'attente. Veuillez vérifier manuellement.');
        setState(PAYMENT_STATES.ERROR);
        return;
      }
      try {
        const response = await apiService.get(`/payments/${payId}`);
        if (!response.payment) return;
        const status = response.payment.status;
        if (status === 'completed') {
          setState(PAYMENT_STATES.SUCCESS);
          onSuccess({ paymentId: payId, amount: response.payment.amount, planName: response.payment.plan_name });
        } else if (status === 'failed' || status === 'cancelled') {
          setError(response.payment.error_message || 'Le paiement a échoué.');
          setState(PAYMENT_STATES.ERROR);
        } else {
          setPollingTries(tries + 1);
          setTimeout(() => checkPaymentStatus(payId, tries + 1), POLLING_INTERVAL);
        }
      } catch (err) {
        setPollingTries(tries + 1);
        setTimeout(() => checkPaymentStatus(payId, tries + 1), POLLING_INTERVAL);
      }
    },
    [onSuccess]
  );

  const handleInitiatePayment = async (e) => {
    e.preventDefault();
    setError(null);
    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Veuillez entrer un numéro valide');
      return;
    }
    setState(PAYMENT_STATES.PROCESSING);
    setPollingTries(0);
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      const userId = currentUser?.user_id;
      if (!userId) { setError('Utilisateur non identifié'); setState(PAYMENT_STATES.ERROR); return; }
      const response = await apiService.post('/payments/serdipay/initiate', {
        user_id: userId,
        plan_type: planName.toLowerCase().replace(/\s+/g, '-'),
        plan_name: planName,
        amount: amount,
        phone_number: phoneNumber,
        duration_days: duration,
        telecom: selectedOperator,
        ...(rideId ? { ride_id: rideId } : {}),
      });
      if (response.success && response.payment_id) {
        setPaymentId(response.payment_id);
        checkPaymentStatus(response.payment_id, 0);
      } else {
        setError(response.error || 'Erreur lors de l\'initiation du paiement');
        setState(PAYMENT_STATES.ERROR);
      }
    } catch (err) {
      console.error('❌ Erreur paiement:', err);
      setError(err?.error || err?.message || 'Erreur de connexion. Vérifiez votre connexion internet.');
      setState(PAYMENT_STATES.ERROR);
    }
  };

  const handleReset = () => { setState(PAYMENT_STATES.FORM); setPhoneNumber(''); setPaymentId(null); setError(null); setPollingTries(0); };
  const handleClose = () => { handleReset(); onCancel(); };

  if (!isOpen) return null;

  const op = OPERATORS[selectedOperator];
  const progress = Math.min((pollingTries / POLLING_MAX_TRIES) * 100, 100);

  return (
    <>
      <style>{`
        .pm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 10000;
          animation: pmFadeIn 0.3s ease;
        }
        @keyframes pmFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pmSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes pmPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes pmSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pmBounceIn { 0% { transform: scale(0.3); opacity: 0; } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes pmProgress { from { width: 0%; } }

        .pm-modal {
          background: #fff;
          border-radius: 24px 24px 0 0;
          width: 100%;
          max-width: 480px;
          max-height: 92vh;
          overflow-y: auto;
          animation: pmSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 -10px 40px rgba(0,0,0,0.15);
        }

        .pm-handle {
          display: flex;
          justify-content: center;
          padding: 12px 0 4px;
        }
        .pm-handle div {
          width: 40px;
          height: 4px;
          background: #ddd;
          border-radius: 4px;
        }

        .pm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 20px 16px;
        }
        .pm-header h2 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1a1a2e;
          margin: 0;
        }
        .pm-close {
          background: #f5f5f5;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s;
        }
        .pm-close:hover { background: #e8e8e8; color: #333; }

        .pm-body { padding: 0 20px 24px; }

        /* Plan Card */
        .pm-plan-card {
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 20px;
          color: white;
          position: relative;
          overflow: hidden;
        }
        .pm-plan-card::before {
          content: '';
          position: absolute;
          top: -30%;
          right: -20%;
          width: 120px;
          height: 120px;
          background: rgba(255,255,255,0.1);
          border-radius: 50%;
        }
        .pm-plan-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          opacity: 0.85;
          margin-bottom: 4px;
        }
        .pm-plan-name {
          font-size: 1.3rem;
          font-weight: 800;
          margin-bottom: 12px;
        }
        .pm-plan-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .pm-plan-amount {
          font-size: 1.8rem;
          font-weight: 800;
          line-height: 1;
        }
        .pm-plan-amount span {
          font-size: 0.9rem;
          font-weight: 500;
          opacity: 0.85;
          margin-left: 4px;
        }
        .pm-plan-duration {
          background: rgba(255,255,255,0.2);
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        /* Operators */
        .pm-section-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 10px;
        }
        .pm-operators {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 20px;
        }
        .pm-op-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 14px;
          border-radius: 12px;
          border: 2px solid #e8e8e8;
          background: #fafafa;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'Poppins', sans-serif;
          font-size: 0.85rem;
          font-weight: 500;
          color: #444;
        }
        .pm-op-btn:hover { border-color: #bbb; }
        .pm-op-btn.selected {
          border-color: var(--op-border);
          background: var(--op-bg);
          color: var(--op-text);
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .pm-op-icon {
          font-size: 1.1rem;
          line-height: 1;
        }

        /* Phone input */
        .pm-phone-group { margin-bottom: 20px; }
        .pm-phone-input-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #f8f9fa;
          border: 2px solid #e8e8e8;
          border-radius: 14px;
          padding: 14px 16px;
          transition: all 0.2s;
        }
        .pm-phone-input-wrap:focus-within {
          border-color: #F54119;
          background: #f0f3ff;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }
        .pm-phone-input-wrap svg { color: #999; flex-shrink: 0; }
        .pm-phone-input {
          flex: 1;
          border: none;
          background: transparent;
          font-size: 1.05rem;
          font-family: 'Space Mono', 'Courier New', monospace;
          letter-spacing: 1px;
          color: #1a1a2e;
          outline: none;
        }
        .pm-phone-input::placeholder { color: #bbb; letter-spacing: 0; font-family: 'Poppins', sans-serif; font-size: 0.9rem; }
        .pm-phone-hint {
          font-size: 0.75rem;
          color: #999;
          margin-top: 8px;
          padding-left: 4px;
        }

        /* Submit Button */
        .pm-submit-btn {
          width: 100%;
          padding: 16px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, #F54119 0%, #FF5500 100%);
          color: white;
          font-size: 1rem;
          font-weight: 700;
          font-family: 'Poppins', sans-serif;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }
        .pm-submit-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5); }
        .pm-submit-btn:active { transform: translateY(0); }
        .pm-submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* Security footer */
        .pm-security {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #f0f0f0;
        }
        .pm-security svg { color: #10b981; }
        .pm-security span { font-size: 0.78rem; color: #888; }

        /* Error message */
        .pm-error {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 10px;
          padding: 10px 14px;
          margin-bottom: 16px;
          color: #dc2626;
          font-size: 0.85rem;
        }

        /* Processing state */
        .pm-processing { text-align: center; padding: 30px 0; }
        .pm-spinner-wrap {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto 20px;
        }
        .pm-spinner {
          width: 80px;
          height: 80px;
          border: 4px solid #e8e8e8;
          border-top-color: #F54119;
          border-radius: 50%;
          animation: pmSpin 1s linear infinite;
        }
        .pm-spinner-icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 1.5rem;
        }
        .pm-processing h3 {
          font-size: 1.15rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 8px;
        }
        .pm-processing p { color: #666; font-size: 0.9rem; margin-bottom: 4px; }
        .pm-progress-bar {
          background: #f0f0f0;
          border-radius: 10px;
          height: 6px;
          margin: 20px 0 8px;
          overflow: hidden;
        }
        .pm-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #F54119, #FF5500);
          border-radius: 10px;
          transition: width 0.5s ease;
          animation: pmProgress 0.5s ease;
        }
        .pm-progress-text { font-size: 0.75rem; color: #aaa; }
        .pm-pin-hint {
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 10px;
          padding: 12px;
          margin-top: 16px;
          font-size: 0.85rem;
          color: #92400e;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Success state */
        .pm-success { text-align: center; padding: 20px 0; }
        .pm-success-icon {
          animation: pmBounceIn 0.6s ease;
          margin-bottom: 16px;
        }
        .pm-success h3 {
          font-size: 1.4rem;
          font-weight: 800;
          color: #059669;
          margin-bottom: 8px;
        }
        .pm-success p { color: #666; font-size: 0.9rem; margin-bottom: 16px; }
        .pm-success-details {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
        }
        .pm-success-details .row {
          display: flex;
          justify-content: space-between;
          padding: 6px 0;
        }
        .pm-success-details .label { color: #666; font-size: 0.85rem; }
        .pm-success-details .value { font-weight: 700; color: #1a1a2e; font-size: 0.9rem; }
        .pm-success-btn {
          width: 100%;
          padding: 16px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, #059669, #10b981);
          color: white;
          font-size: 1rem;
          font-weight: 700;
          font-family: 'Poppins', sans-serif;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(5, 150, 105, 0.4);
          transition: all 0.2s;
        }
        .pm-success-btn:hover { transform: translateY(-1px); }

        /* Error state */
        .pm-error-state { text-align: center; padding: 20px 0; }
        .pm-error-state h3 {
          font-size: 1.2rem;
          font-weight: 700;
          color: #dc2626;
          margin: 16px 0 8px;
        }
        .pm-error-state p { color: #666; font-size: 0.9rem; margin-bottom: 20px; }
        .pm-error-state .pm-btn-group { display: flex; flex-direction: column; gap: 10px; }
        .pm-retry-btn {
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 14px;
          background: linear-gradient(135deg, #F54119, #FF5500);
          color: white;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: 'Poppins', sans-serif;
          cursor: pointer;
          transition: all 0.2s;
        }
        .pm-cancel-btn {
          width: 100%;
          padding: 14px;
          border: 2px solid #e8e8e8;
          border-radius: 14px;
          background: transparent;
          color: #666;
          font-size: 0.95rem;
          font-weight: 600;
          font-family: 'Poppins', sans-serif;
          cursor: pointer;
          transition: all 0.2s;
        }
        .pm-cancel-btn:hover { background: #f5f5f5; }

        @media (min-width: 500px) {
          .pm-overlay { align-items: center; }
          .pm-modal { border-radius: 24px; max-height: 90vh; }
        }
      `}</style>

      <div className="pm-overlay" onClick={handleClose}>
        <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="pm-handle"><div /></div>
          
          <div className="pm-header">
            <h2>💳 Paiement</h2>
            <button className="pm-close" onClick={handleClose}>
              <X size={18} />
            </button>
          </div>

          <div className="pm-body">
            {/* Plan Card - always visible */}
            <div className="pm-plan-card">
              <div className="pm-plan-label">Abonnement sélectionné</div>
              <div className="pm-plan-name">{planName}</div>
              <div className="pm-plan-row">
                <div className="pm-plan-amount">
                  {amount.toLocaleString('fr-FR')}<span>CDF</span>
                </div>
                <div className="pm-plan-duration">📅 {duration} jours</div>
              </div>
            </div>

            {/* Error display */}
            {error && state === PAYMENT_STATES.FORM && (
              <div className="pm-error">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            {/* FORM STATE */}
            {state === PAYMENT_STATES.FORM && (
              <form onSubmit={handleInitiatePayment}>
                <div className="pm-section-title">Opérateur mobile</div>
                <div className="pm-operators">
                  {Object.entries(OPERATORS).map(([key, o]) => (
                    <button
                      key={key}
                      type="button"
                      className={`pm-op-btn ${selectedOperator === key ? 'selected' : ''}`}
                      style={{
                        '--op-bg': o.bg,
                        '--op-border': o.border,
                        '--op-text': o.text,
                      }}
                      onClick={() => setSelectedOperator(key)}
                    >
                      <span className="pm-op-icon">{o.icon}</span>
                      {o.name}
                    </button>
                  ))}
                </div>

                <div className="pm-phone-group">
                  <div className="pm-section-title">Numéro {op.name}</div>
                  <div className="pm-phone-input-wrap">
                    <Phone size={20} />
                    <input
                      type="tel"
                      className="pm-phone-input"
                      placeholder="+243 9XX XXX XXX"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      autoFocus
                    />
                  </div>
                  <div className="pm-phone-hint">Format: +243... (RDC) ou numéro local</div>
                </div>

                <button
                  type="submit"
                  className="pm-submit-btn"
                  disabled={!phoneNumber || phoneNumber.length < 10}
                >
                  Payer {amount.toLocaleString('fr-FR')} CDF
                </button>

                <div className="pm-security">
                  <Shield size={14} />
                  <span>Paiement sécurisé via {op.name}</span>
                </div>
              </form>
            )}

            {/* PROCESSING STATE */}
            {state === PAYMENT_STATES.PROCESSING && (
              <div className="pm-processing">
                <div className="pm-spinner-wrap">
                  <div className="pm-spinner" />
                  <div className="pm-spinner-icon">{op.icon}</div>
                </div>
                <h3>Paiement en cours</h3>
                <p>Vérification auprès de {op.name}...</p>

                <div className="pm-progress-bar">
                  <div className="pm-progress-fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="pm-progress-text">
                  Vérification {pollingTries + 1} / {POLLING_MAX_TRIES}
                </div>

                <div className="pm-pin-hint">
                  📲 Entrez votre code PIN {op.name} si un prompt apparaît sur votre téléphone
                </div>
              </div>
            )}

            {/* SUCCESS STATE */}
            {state === PAYMENT_STATES.SUCCESS && (
              <div className="pm-success">
                <div className="pm-success-icon">
                  <CheckCircle size={64} color="#059669" />
                </div>
                <h3>Paiement réussi ! 🎉</h3>
                <p>Votre abonnement a été activé avec succès</p>

                <div className="pm-success-details">
                  <div className="row">
                    <span className="label">Plan</span>
                    <span className="value">{planName}</span>
                  </div>
                  <div className="row">
                    <span className="label">Montant</span>
                    <span className="value">{amount.toLocaleString('fr-FR')} CDF</span>
                  </div>
                  <div className="row">
                    <span className="label">Durée</span>
                    <span className="value">{duration} jours</span>
                  </div>
                  <div className="row">
                    <span className="label">Opérateur</span>
                    <span className="value">{op.icon} {op.name}</span>
                  </div>
                </div>

                <button className="pm-success-btn" onClick={handleClose}>
                  Continuer →
                </button>
              </div>
            )}

            {/* ERROR STATE */}
            {state === PAYMENT_STATES.ERROR && (
              <div className="pm-error-state">
                <AlertCircle size={56} color="#dc2626" />
                <h3>Échec du paiement</h3>
                <p>{error}</p>

                <div className="pm-btn-group">
                  <button className="pm-retry-btn" onClick={handleReset}>
                    🔄 Réessayer
                  </button>
                  <button className="pm-cancel-btn" onClick={handleClose}>
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
